import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import queryString from 'query-string';
import Bicycle from '@deliveroo/icon-set/react/Bicycle';
import Ticket from '@deliveroo/icon-set/react/Ticket';
import ForkKnife from '@deliveroo/icon-set/react/ForkKnife';
import Mansion from '@deliveroo/icon-set/react/Mansion';
import ConsumerNavHeader from '@deliveroo/consumer-component-library/components/ConsumerNavHeader';

import { showInviteModal } from '../../actions/inviteActions';
import CookieBanner from '../CookieBanner';
import i18n from '../../utils/i18n';
import { urlWithLocale } from '../../utils/locale';
import { getAlternateLanguageUrls } from '../../selectors/navSelectors';
import { isFeatureActive, hasVariant } from '../../selectors/configSelectors';
import { DELOVEROO_LOGO, ONETRUST_COOKIE_CONSENT } from '../../flags/features';
import { RS_WORK_WITH_US_DROPDOWN } from '../../flags/experiments';

const rideWithUsParams = {
  'utm-campaign': 'ridewithus',
  'utm-medium': 'organic',
  'utm-source': 'landingpage',
};

const restaurantParams = {
  'utm-campaign': 'workwithus',
  'utm-medium': 'organic',
  'utm-source': 'landingpage',
};

export class Header extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      menuButtonOpen: false,
    };
  }

  handleMenuButtonClick = () => {
    this.setState((state) => ({ menuButtonOpen: !state.menuButtonOpen }));
  };

  getRidersLink = (isShort) => ({
    href: this.navUrl(`/apply?${queryString.stringify(rideWithUsParams)}`),
    sidebarLabel: i18n.gettext('landing_index_page.header.invite_rider_cta'),
    label: i18n.gettext(
      isShort
        ? 'landing_index_page.header.rider_cta'
        : 'landing_index_page.header.invite_rider_cta',
    ),
    icon: Bicycle,
  });

  getRestaurantsLink = () => ({
    href: `${this.props.restaurantLink}?${queryString.stringify(restaurantParams)}`,
    sidebarLabel: i18n.gettext('landing_index_page.header.restaurant_sidebar_cta'),
    label: i18n.gettext('landing_index_page.header.restaurant_cta'),
    icon: ForkKnife,
  });

  getCareersLink = () => ({
    href: this.props.careersLink,
    label: i18n.gettext('landing_index_page.header.career_cta'),
    icon: Mansion,
    showInSidebar: false,
  });

  getRooviteLink = () => ({
    onClick: this.props.clickInviteLink,
    label: i18n.gettext('landing_index_page.header.mgm_cta_label'),
    icon: Ticket,
  });

  navLinks() {
    const { isHomepage, shouldEnableWorkWithUsDropdown, rooviteLink, riderInviteLink } = this.props;
    const links = [];

    if (isHomepage) {
      if (shouldEnableWorkWithUsDropdown) {
        const dropdownLinks = [];
        if (riderInviteLink) {
          dropdownLinks.push(this.getRidersLink(true));
        }
        dropdownLinks.push(this.getRestaurantsLink());
        dropdownLinks.push(this.getCareersLink());

        links.push({
          label: i18n.gettext('landing_index_page.header.cta_drop_down_button'),
          links: dropdownLinks,
          onClick: this.handleMenuButtonClick,
          isMenuOpen: this.state.menuButtonOpen,
        });
      } else if (riderInviteLink) {
        links.push(this.getRidersLink(false));
      }

      if (rooviteLink) {
        links.push(this.getRooviteLink());
      }
    }

    return links;
  }

  navUrl(url) {
    const { languages, locale } = this.props;
    return urlWithLocale({ languages }, url, locale);
  }

  render() {
    const {
      alternateLanguages,
      countries,
      isTransparent,
      logoColor,
      accountEmail,
      basket,
      currentUrl,
      tld,
      maxWidth,
      appElement,
      isDeloverooLogoEnabled,
      searchComponent,
      shouldShowOldCookieBanner,
    } = this.props;

    let logoVariant = null;
    if (['hk', 'tw'].includes(tld)) logoVariant = 'chinese';
    if (isDeloverooLogoEnabled) logoVariant = 'deloveroo';

    const links = this.navLinks();

    return (
      <Fragment>
        {shouldShowOldCookieBanner && <CookieBanner />}
        <ConsumerNavHeader
          key={JSON.stringify(links)}
          links={links}
          orderHistory={{
            label: i18n.gettext('navheader.order_history.label'),
            href: this.navUrl('/orders'),
          }}
          account={{
            label: i18n.gettext('navheader.account.label'),
            href: this.navUrl('/account'),
          }}
          accountEmail={accountEmail}
          logout={{
            label: i18n.gettext('navheader.log_out.label'),
            href: this.navUrl(`/logout?redirect=${encodeURIComponent(currentUrl)}`),
          }}
          login={{
            label: i18n.gettext('navheader.log_in.label'),
            href: this.navUrl(`/login?redirect=${encodeURIComponent(currentUrl)}`),
          }}
          menuLabel={i18n.gettext('navheader.menu_button')}
          languages={alternateLanguages}
          countries={countries.map((country) => ({
            label: country.name,
            href: `/session-transfer?to=${encodeURIComponent(`https://${country.host}`)}`,
            selected: country.id === tld,
          }))}
          basket={{
            value: basket.subtotal,
            href: basket.url,
          }}
          isSolidBar={!isTransparent}
          logoHomeLink={this.navUrl('/')}
          logoColor={logoColor}
          logoVariant={logoVariant}
          appElement={appElement}
          containerMaxWidth={maxWidth}
          closeButtonLabel={i18n.gettext('closeButton.label')}
          searchComponent={searchComponent}
        />
      </Fragment>
    );
  }
}

Header.propTypes = {
  isTransparent: PropTypes.bool,
  isHomepage: PropTypes.bool,
  isDeloverooLogoEnabled: PropTypes.bool,
  logoColor: PropTypes.oneOf(['teal', 'white']),
  rooviteLink: PropTypes.string,
  riderInviteLink: PropTypes.bool,
  restaurantLink: PropTypes.string,
  careersLink: PropTypes.string,
  clickInviteLink: PropTypes.func.isRequired,
  tld: PropTypes.string.isRequired,
  maxWidth: PropTypes.string,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  locale: PropTypes.string.isRequired,
  alternateLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      selected: PropTypes.bool,
    }),
  ).isRequired,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      host: PropTypes.string.isRequired,
    }),
  ).isRequired,
  currentUrl: PropTypes.string.isRequired,
  accountEmail: PropTypes.string,
  basket: PropTypes.shape({
    subtotal: PropTypes.string,
    url: PropTypes.string,
  }),
  appElement: PropTypes.string,
  searchComponent: PropTypes.node,
  shouldShowOldCookieBanner: PropTypes.bool.isRequired,
  shouldEnableWorkWithUsDropdown: PropTypes.bool,
};

Header.defaultProps = {
  isTransparent: false,
  isHomepage: false,
  isDeloverooLogoEnabled: false,
  logoColor: 'teal',
  rooviteLink: null,
  restaurantLink: null,
  careersLink: null,
  riderInviteLink: false,
  maxWidth: 'default',
  accountEmail: null,
  basket: {},
  appElement: '#__next',
  searchComponent: null,
  shouldEnableWorkWithUsDropdown: false,
};

export default connect(
  (state) => ({
    rooviteLink: state.user.roovite.link,
    riderInviteLink: state.request.country.riderInviteLink,
    restaurantLink: state.request.country.restaurantsHomepageUrl,
    careersLink: (
      state.config.countries.find((country) => country.id === state.request.country.tld) || {}
    ).careersLink,
    tld: state.request.country.tld,
    isDeloverooLogoEnabled: isFeatureActive(state, DELOVEROO_LOGO),
    alternateLanguages: getAlternateLanguageUrls(state),
    languages: state.request.country.languages,
    locale: state.request.locale,
    countries: state.config.countries,
    currentUrl: state.request.currentUrl,
    accountEmail: state.user.email,
    basket: state.basket,
    // if the new OneTrust cookie banner is active,
    // don't show the old CookieBanner component
    shouldShowOldCookieBanner: !isFeatureActive(state, ONETRUST_COOKIE_CONSENT),
    shouldEnableWorkWithUsDropdown: hasVariant(state, RS_WORK_WITH_US_DROPDOWN, 'work_with_us'),
  }),
  (dispatch) => ({
    clickInviteLink: () => dispatch(showInviteModal()),
  }),
)(Header);
